import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, money } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton, Badge } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, Plus, Sparkle, ArrowRight } from "@phosphor-icons/react";

export default function Contracts() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", contractor_id: "", amount: "", start_date: "", end_date: "", description: "", anticipo_percent: "0", retencion_percent: "0" });
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);

  const load = () => api.get("/contracts").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/contractors").then((r) => setContractors(r.data)); }, []);

  const onFileChange = async (f) => {
    setFile(f);
    if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/contracts/extract-preview", fd);
      if (data.error) { toast.info(data.error); return; }
      setForm((prev) => ({
        ...prev,
        amount: data.monto_total != null ? String(data.monto_total) : prev.amount,
        anticipo_percent: data.anticipo_percent != null ? String(data.anticipo_percent) : prev.anticipo_percent,
        retencion_percent: data.fondo_garantia_percent != null ? String(data.fondo_garantia_percent) : prev.retencion_percent,
        start_date: data.fecha_inicio || prev.start_date,
        end_date: data.fecha_termino || prev.end_date,
      }));
      toast.success("Datos extraídos del contrato. Revisa y corrige si es necesario.");
    } catch (err) {
      toast.info("No se pudieron extraer los datos. Captúralos manualmente.");
    } finally { setExtracting(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.contractor_id) return toast.error("Selecciona un contratista");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Captura el monto (súbelo del PDF o escríbelo)");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try {
      await api.post("/contracts", fd);
      toast.success(file ? "Contrato subido y analizado con IA" : "Contrato creado");
      setOpen(false); setForm({ title: "", contractor_id: "", amount: "", start_date: "", end_date: "", description: "", anticipo_percent: "0", retencion_percent: "0" }); setFile(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader overline="Contratos" title="Contratos"
        action={isManager && (
          <div className="flex items-center gap-2">
            <button onClick={() => window.open(`${api.defaults.baseURL}/export/contracts.csv`, "_blank")} data-testid="export-contracts-csv"
              className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
              <FileText size={18} weight="bold" /> Exportar CSV
            </button>
            <PrimaryButton onClick={() => setOpen(true)} data-testid="add-contract-btn"><Plus size={18} weight="bold" /> Nuevo</PrimaryButton>
          </div>
        )} />

      {items.length === 0 ? (
        <EmptyState icon={FileText} title="Sin contratos" subtitle="Sube el contrato en PDF y la IA generará el resumen, fechas y penalizaciones."
          action={isManager && <PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Subir contrato</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((c) => (
            <Link key={c.id} to={`/contratos/${c.id}`} data-testid={`contract-card-${c.id}`}>
              <Card className="p-6 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200 h-full">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading font-bold text-lg leading-tight">{c.title}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{c.contractor_name}</div>
                  </div>
                  <Badge status={c.status} />
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="overline">Monto</div>
                    <div className="font-heading font-extrabold text-2xl text-primary">{money(c.amount)}</div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    {c.start_date && <div>Inicio: {c.start_date}</div>}
                    {c.end_date && <div>Término: {c.end_date}</div>}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Estimado acumulado</span>
                    <span className="font-semibold" data-testid={`contract-progress-${c.id}`}>{money(c.estimated_gross || 0)} · {c.progress_pct || 0}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-sm overflow-hidden">
                    <div className={`h-full ${(c.progress_pct || 0) > 100 ? "bg-danger" : "bg-primary"}`} style={{ width: `${Math.min(100, c.progress_pct || 0)}%` }} />
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-muted-foreground">Saldo por estimar</span>
                    <span className={`font-semibold ${(c.saldo || 0) < 0 ? "text-danger" : ""}`} data-testid={`contract-saldo-${c.id}`}>{money(c.saldo || 0)}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  {c.ai_analysis && !c.ai_analysis.error ? (
                    <span className="inline-flex items-center gap-1 text-primary font-medium"><Sparkle size={16} weight="fill" /> Analizado con IA</span>
                  ) : c.pdf_name ? <span className="text-muted-foreground">PDF adjunto</span> : <span className="text-muted-foreground">Sin PDF</span>}
                  <span className="inline-flex items-center gap-1 text-muted-foreground">Ver detalle <ArrowRight size={15} /></span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo contrato">
        <form onSubmit={save} className="space-y-4">
          <Field label="Título *"><input data-testid="contract-title" required className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Contratista *">
            <select data-testid="contract-contractor" required className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
              <option value="">Selecciona…</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Monto total (MXN) *">
            <input data-testid="contract-amount" type="number" step="0.01" required className={inputClass} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            {file && <p className="text-xs text-muted-foreground mt-1">Se extrae del contrato (PDF) con IA. Puedes corregirlo aquí si detectas un error.</p>}
          </Field>
          <Field label="Anticipo (%) — se amortiza en cada estimación">
            <input data-testid="contract-anticipo" type="number" step="1" min="0" max="100" className={inputClass} value={form.anticipo_percent} onChange={(e) => setForm({ ...form, anticipo_percent: e.target.value })} />
          </Field>
          <Field label="Fondo de garantía / retención (%) — se retiene en cada estimación">
            <input data-testid="contract-retencion" type="number" step="1" min="0" max="100" className={inputClass} value={form.retencion_percent} onChange={(e) => setForm({ ...form, retencion_percent: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Fecha inicio"><input type="date" className={inputClass} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Fecha término"><input type="date" className={inputClass} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          </div>
          <Field label="Contrato PDF (extracción automática con IA)">
            <input data-testid="contract-file" type="file" accept="application/pdf" onChange={(e) => onFileChange(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            {extracting && <p className="text-xs text-primary mt-1 font-medium" data-testid="contract-extracting">Leyendo contrato… extrayendo monto, anticipo, fondo de garantía y fechas.</p>}
          </Field>
          <p className="text-xs text-muted-foreground">Al subir el PDF, la IA llena automáticamente el monto, anticipo, fondo de garantía y las fechas (editables). El resumen, obligaciones y penalizaciones se analizan al guardar.</p>
          <PrimaryButton type="submit" disabled={busy || extracting} data-testid="save-contract-btn">{busy ? "Procesando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
